import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusIcon, ArrowLeftIcon, BookOpenIcon, DocumentTextIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useFirestore } from '../hooks/useFirestore';
import DocumentTable from '../components/DocumentTable';
import DocumentModal from '../components/DocumentModal';
import ConfirmDialog from '../components/ConfirmDialog';

const CollectionPage = () => {
  const { collectionName } = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    documents,
    loading,
    addDocument,
    updateDocument,
    deleteDocument
  } = useFirestore(collectionName);

  // Collections that have subcollections
  const collectionsWithSubcollections = {
    'Mybooks': ['books'],
    'classrooms': ['messages', 'privateMessages'],
    'users': ['Courses']
  };

  const hasSubcollections = collectionsWithSubcollections[collectionName];

  const handleAdd = () => {
    setSelectedDocument(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleEdit = (doc) => {
    setSelectedDocument(doc);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleView = (doc) => {
    setSelectedDocument(doc);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDocument(deleteId);
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (modalMode === 'add') {
      await addDocument(data);
    } else if (modalMode === 'edit') {
      await updateDocument(selectedDocument.id, data);
    }
  };

  const handleViewSubcollection = (doc) => {
    // Navigate to subcollection view
    const subcollectionName = hasSubcollections[0]; // Use first subcollection
    navigate(`/collection/${collectionName}/${doc.id}/${subcollectionName}`);
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-6 p-3 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-4xl font-bold text-gray-900 capitalize">
                    {collectionName}
                  </h1>
                  {hasSubcollections && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg">
                      <BookOpenIcon className="h-4 w-4 mr-1" />
                      Has Subcollections
                    </span>
                  )}
                </div>
                <p className="text-lg text-gray-600 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  Manage your {collectionName} collection data
                </p>
              </div>
            </div>
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Document
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 overflow-hidden shadow-xl rounded-xl">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium uppercase tracking-wide">
                  Total Documents
                </p>
                <p className="text-3xl font-bold">
                  {documents.length.toLocaleString()}
                </p>
              </div>
                              <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
            </div>
          </div>
        </div>

        {hasSubcollections && (
          <div className="bg-gradient-to-br from-green-500 to-green-600 overflow-hidden shadow-xl rounded-xl">
            <div className="p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium uppercase tracking-wide">
                    Subcollections
                  </p>
                  <p className="text-3xl font-bold">
                    {hasSubcollections.length}
                  </p>
                  <p className="text-green-100 text-xs mt-1">
                    {hasSubcollections.join(', ')}
                  </p>
                </div>
                <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
                  <BookOpenIcon className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`bg-gradient-to-br ${loading ? 'from-yellow-500 to-yellow-600' : 'from-green-500 to-green-600'} overflow-hidden shadow-xl rounded-xl`}>
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${loading ? 'text-yellow-100' : 'text-green-100'} text-sm font-medium uppercase tracking-wide`}>
                  Status
                </p>
                <p className="text-3xl font-bold flex items-center">
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading
                    </>
                  ) : (
                    'Ready'
                  )}
                </p>
              </div>
              <div className={`${loading ? 'bg-yellow-400' : 'bg-green-400'} bg-opacity-30 rounded-full p-3`}>
                {loading ? (
                  <ExclamationTriangleIcon className="h-8 w-8 text-white" />
                ) : (
                  <CheckCircleIcon className="h-8 w-8 text-white" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 overflow-hidden shadow-xl rounded-xl">
          <div className="p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium uppercase tracking-wide">
                  Last Updated
                </p>
                <p className="text-lg font-semibold">
                  {new Date().toLocaleDateString()}
                </p>
                <p className="text-purple-100 text-xs">
                  {new Date().toLocaleTimeString()}
                </p>
              </div>
              <div className="bg-purple-400 bg-opacity-30 rounded-full p-3">
                <ClockIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <DocumentTable
        documents={documents}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        onViewSubcollection={hasSubcollections ? handleViewSubcollection : null}
        collectionName={collectionName}
        hasSubcollections={!!hasSubcollections}
      />

      {/* Document Modal */}
      <DocumentModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        document={selectedDocument}
        collectionName={collectionName}
        mode={modalMode}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        loading={deleteLoading}
      />
    </div>
  );
};

export default CollectionPage;
